import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AuthLayout from '@shared/layouts/AuthLayout';

const TermsOfService: React.FC = () => {
  const { t } = useTranslation('legal');

  const tocItems = t('termsOfService.tocItems', { returnObjects: true }) as string[];
  const sections = t('termsOfService.sections', {
    returnObjects: true,
  }) as Array<{
    id: string;
    number: string;
    title: string;
    paragraphs: string[];
    listItems: string[];
  }>;

  return (
    <AuthLayout maxWidth="max-w-4xl">
      <div className="bg-surface-card-light dark:bg-surface-card-dark rounded-3xl border border-surface-border-light dark:border-surface-border-dark shadow-soft p-8 sm:p-12">
        <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight mb-2">
          {t('termsOfService.title')}
        </h1>
        <p className="text-xs font-bold text-text-subtle-light dark:text-text-subtle-dark uppercase tracking-widest mb-8">
          {t('termsOfService.lastUpdated')}
        </p>

        {/* Table of Contents */}
        <nav className="mb-10 p-6 bg-surface-2-light dark:bg-surface-2-dark rounded-2xl border border-surface-border-light dark:border-surface-border-dark">
          <h2 className="text-xs font-black text-text-muted-light dark:text-text-muted-dark uppercase tracking-widest mb-4">
            {t('termsOfService.tableOfContentsTitle')}
          </h2>
          <ol className="space-y-1.5 pl-5 text-sm text-brand-600 dark:text-brand-400 font-medium list-decimal">
            {tocItems.map((item, index) => (
              <li key={index}>
                <a
                  href={`#${sections[index]?.id}`}
                  className="block min-h-11 rounded-sm py-3 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 dark:focus-visible:ring-brand-400"
                >
                  {item}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <div className="space-y-8 text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
          <p>{t('termsOfService.intro')}</p>

          {sections.map((section) => (
            <Section key={section.id} id={section.id} number={section.number} title={section.title}>
              {section.paragraphs.map((paragraph, pIndex) => (
                <p key={pIndex} className={pIndex > 0 ? 'mt-3' : undefined}>
                  {paragraph}
                </p>
              ))}
              {section.listItems.length > 0 && (
                <ul className="list-disc list-inside space-y-1 mt-2 ml-4">
                  {section.listItems.map((item, lIndex) => (
                    <li key={lIndex}>{item}</li>
                  ))}
                </ul>
              )}
            </Section>
          ))}
        </div>

        {/* Footer navigation */}
        <div className="mt-10 pt-8 border-t border-surface-border-light dark:border-surface-border-dark flex flex-wrap gap-6 text-xs font-bold text-text-subtle-light dark:text-text-subtle-dark uppercase tracking-widest">
          <Link
            to="/privacy"
            className="hover:text-brand-600 dark:hover:text-brand-400 transition-colors"
          >
            {t('termsOfService.footer.privacyPolicy')}
          </Link>
          <Link
            to="/cookies"
            className="hover:text-brand-600 dark:hover:text-brand-400 transition-colors"
          >
            {t('termsOfService.footer.cookiePolicy')}
          </Link>
        </div>
      </div>
    </AuthLayout>
  );
};

function Section({
  id,
  number,
  title,
  children,
}: {
  id: string;
  number: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id}>
      <h2 className="text-lg font-black text-slate-900 dark:text-white tracking-tight mb-3">
        {number}. {title}
      </h2>
      {children}
    </section>
  );
}

export default TermsOfService;
